import clsx from 'clsx'
import { nothing, unsafeCSS } from 'lit'
import { property, queryAssignedNodes, state } from 'lit/decorators.js'
import { html } from 'lit/static-html.js'

import { t } from '@/localization/localization'

import { DdsElement } from '../../internal/dds-hu-element'
import { SizedMixin } from '../../internal/mixin/sizedMixin'
import DapDSIconButton from '../icon-button/icon-button.component'
import DapDSStack from '../stack/stack.component'
import styles from './chip.scss?inline'

const ChipBaseWithSized = SizedMixin(DdsElement)

export type ChipSize = 'sm' | 'lg'

/**
 * `dap-ds-chip`
 * @summary A chip is a small status descriptor for UI elements.
 * @element dap-ds-chip
 * @title - Chip
 *
 * @property { 'sm' | 'lg' } size - The size of the chip. Default is `sm`.
 * @property {string} sizeMap - Responsive size map (e.g. "md:lg").
 *
 * @event {{ value: string }} dds-remove - Fired when the chip is removed
 * @event {{ value?: string, selected: boolean }} dds-select - Fired when the chip is selected
 *
 * @csspart base - The base part
 * @csspart content-container - The container for the content
 * @csspart remove-button - The remove button
 * @csspart remove-icon - The icon of the remove icon
 * @csspart remove-icon-base - The base of the remove icon
 * @csspart remove-icon-base-base - The base of the remove icon base
 *
 * @cssproperty --dds-chip-border - Border of the chip (default: var(--dds-border-width-base) solid var(--dds-transparent-black-base))
 * @cssproperty --dds-chip-border-radius - Border radius of the chip (default: var(--dds-radius-small))
 * @cssproperty --dds-chip-font-weight - Font weight of the chip (default: var(--dds-font-weight-bold))
 * @cssproperty --dds-chip-line-height - Line height of the chip (default: 1.5)
 * @cssproperty --dds-chip-transition - Transition timing for chip interactions (default: all 0.2s ease-in-out)
 * @cssproperty --dds-chip-padding-sm - Padding for small chip size (default: var(--dds-spacing-100) var(--dds-spacing-200))
 * @cssproperty --dds-chip-padding-lg - Padding for large chip size (default: var(--dds-spacing-200) var(--dds-spacing-300))
 * @cssproperty --dds-chip-font-size-sm - Font size for small chip size (default: var(--dds-font-xs))
 * @cssproperty --dds-chip-font-size-lg - Font size for large chip size (default: var(--dds-font-xs))
 * @cssproperty --dds-chip-background-color - Background color of the chip (default: var(--dds-background-neutral-base))
 * @cssproperty --dds-chip-text-color - Text color of the chip (default: var(--dds-text-neutral-base))
 * @cssproperty --dds-chip-hover-background-color - Background color of the chip on hover (default: var(--dds-background-neutral-medium))
 * @cssproperty --dds-chip-active-background-color - Background color of the chip when active (default: var(--dds-background-neutral-strong))
 * @cssproperty --dds-chip-selected-background-color - Background color of the selected chip (default: var(--dds-background-brand-medium))
 * @cssproperty --dds-chip-selected-text-color - Text color of the selected chip (default: var(--dds-text-brand-subtle))
 * @cssproperty --dds-chip-selected-border-color - Border color of the selected chip (default: var(--dds-border-brand-base))
 * @cssproperty --dds-chip-disabled-background-color - Background color of the disabled chip (default: var(--dds-background-neutral-medium))
 * @cssproperty --dds-chip-disabled-text-color - Text color of the disabled chip (default: var(--dds-text-neutral-subtle))
 *
 */
export default class DapDSChip extends ChipBaseWithSized {
  static tagName = 'dap-ds-chip'

  static dependencies = {
    'dap-ds-stack': DapDSStack,
    'dap-ds-icon-button': DapDSIconButton,
  }

  /** Maps effectiveSize from SizedMixin to the chip's two visual sizes (sm | lg). */
  private get chipSize(): ChipSize {
    const s = this.effectiveSize
    return s === 'lg' || s === 'md' ? 'lg' : 'sm'
  }

  /** Whether the chip is removeable */
  @property({ type: Boolean }) removeable = false

  /** Whether the chip is selectable */
  @property({ type: Boolean }) selectable = false

  /** Whether the chip is selected */
  @property({ type: Boolean, reflect: true }) selected = false

  /** Whether the chip is disabled */
  @property({ type: Boolean, reflect: true }) disabled = false

  /** The value of the chip */
  @property({ type: String }) value?: string

  /** The aria label for the delete button */
  @property({ type: String }) deleteAriaLabel?: string

  /** `data-testid` for the remove button. */
  @property() removeButtonTestId = 'chip-remove-button'

  @queryAssignedNodes({ slot: '', flatten: true })
  private _slotNodes!: Node[]

  @state() private _slotTextContent = ''

  static readonly styles = unsafeCSS(styles)

  /**
   * Gets the text content from the slot nodes
   */
  private extractSlotTextContent(nodes: Node[]): string {
    if (!nodes || nodes.length === 0) return ''
    return nodes
      .map(node => {
        if (node.nodeType === Node.TEXT_NODE) {
          return node.textContent?.trim() || ''
        }
        if (node.nodeType === Node.ELEMENT_NODE) {
          return (node as Element).textContent?.trim() || ''
        }
        return ''
      })
      .filter(text => text.length > 0)
      .join(' ')
  }

  /**
   * Handles slot content changes
   */
  private handleSlotChange(): void {
    if (this._slotNodes) {
      this._slotTextContent = this.extractSlotTextContent(this._slotNodes)
    }
  }

  /**
   * Gets the aria-label for the chip when removable
   */
  private getRemovableAriaLabel(): string {
    const removableText = t('chip.removable')
    return this._slotTextContent
      ? `${this._slotTextContent}, ${removableText}`
      : removableText
  }

  updated(changedProperties: Map<string, unknown>) {
    super.updated(changedProperties)

    // Update slot text content when slot nodes are available
    if (this._slotNodes && this._slotNodes.length > 0) {
      this.handleSlotChange()
    }

    // Prevent removeable and selectable from being enabled together
    if (
      changedProperties.has('removeable') ||
      changedProperties.has('selectable')
    ) {
      if (this.removeable && this.selectable) {
        console.warn(
          'Chip cannot be both removeable and selectable at the same time.',
        )
        // Disable selectable if removeable is set, or vice versa
        if (changedProperties.has('removeable') && this.removeable) {
          this.selectable = false
        } else if (changedProperties.has('selectable') && this.selectable) {
          this.removeable = false
        }
      }
    }
  }

  private handleClick(event: Event): void {
    event.stopPropagation()
    if (!this.disabled && this.selectable) {
      this.selected = !this.selected
      this.emit('dds-select', { value: this.value, selected: this.selected })
    }
  }

  private handleRemove(event: Event): void {
    event.stopPropagation()
    if (!this.disabled) {
      this.emit('dds-remove', { value: this.value ?? '' })
    }
  }

  private handleKeyDown(event: KeyboardEvent): void {
    if (this.disabled) {
      return
    }

    if (
      (event.key === 'Delete' || event.key === 'Backspace') &&
      this.removeable
    ) {
      event.preventDefault()
      event.stopPropagation()
      this.handleRemove(event)
    }

    if (event.key === 'Enter' && this.selectable) {
      event.preventDefault()
      event.stopPropagation()
      this.handleClick(event)
    }
  }

  render() {
    return html`
      <div
        part="base"
        class=${clsx('chip', `chip--${this.chipSize}`, {
          'chip--selected': this.selectable && this.selected,
          'chip--disabled': this.disabled,
          'chip--selectable': this.selectable,
        })}
        role="button"
        tabindex=${this.disabled ? '-1' : '0'}
        aria-disabled=${this.disabled}
        aria-pressed=${this.selected}
        aria-label=${this.removeable
          ? this.getRemovableAriaLabel()
          : (this.ariaLabel ?? nothing)}
        @click=${this.handleClick}
        @keydown=${this.handleKeyDown}>
        <dap-ds-stack direction="row" spacing="200" part="content-container">
          <slot part="content" @slotchange=${this.handleSlotChange}></slot>
          ${this.removeable
            ? html`<dap-ds-icon-button
                icon="close-line"
                part="remove-button"
                data-testid=${this.removeButtonTestId}
                exportparts="base:remove-icon-base, icon:remove-icon, icon-base:remove-icon-base-base"
                aria-hidden="true"
                tabindex="-1"
                ?disabled=${this.disabled}
                size="sm"
                aria-label=${this.deleteAriaLabel ?? t('chip.remove')}
                @click=${this.handleRemove}></dap-ds-icon-button>`
            : nothing}
        </dap-ds-stack>
      </div>
    `
  }
}
